import { useState } from 'react';
import { Search } from 'lucide-react';
import { usePaymentTowns } from '../../hooks/usePayments';
import { formatNumber } from '@shared/utils/format';
import type { PaymentTownRow } from '@shared/types/payment';

type SortKey = 'amount' | 'diffAmount';

export default function PaymentTable({ ym }: { ym: string }) {
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState<SortKey>('amount');
    const { data } = usePaymentTowns(ym, search);

    const rows: PaymentTownRow[] = [...(data ?? [])].sort((a, b) =>
        sortKey === 'amount' ? b.amount - a.amount : b.diffAmount - a.diffAmount,
    );

    const th = 'px-3 py-2 text-left text-xs font-semibold text-[#73879C] uppercase tracking-wider';
    const td = 'px-3 py-2 text-[13px] text-[#73879C]';

    return (
        <div className="bg-white rounded shadow-sm border border-[#E6E9ED] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6E9ED]">
                <h3 className="text-xs font-bold text-[#2A3F54] uppercase tracking-wider">단지별 결제 내역</h3>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="w-4 h-4 text-[#ADB5BD] absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="단지명 검색"
                            className="pl-8 pr-3 py-1.5 text-sm border border-[#E6E9ED] rounded focus:outline-none focus:ring-2 focus:ring-[#1ABB9C]/20"
                        />
                    </div>
                    <select
                        value={sortKey}
                        onChange={(e) => setSortKey(e.target.value as SortKey)}
                        className="px-2 py-1.5 text-sm border border-[#E6E9ED] rounded bg-white"
                    >
                        <option value="amount">금액순</option>
                        <option value="diffAmount">증감순</option>
                    </select>
                </div>
            </div>
            <table className="w-full">
                <thead className="bg-[#F5F7FA] border-b border-[#E6E9ED]">
                    <tr>
                        <th className={th}>단지명</th>
                        <th className={th}>구분</th>
                        <th className={`${th} text-right`}>일반</th>
                        <th className={`${th} text-right`}>포인트</th>
                        <th className={`${th} text-right`}>선택월</th>
                        <th className={`${th} text-right`}>전월</th>
                        <th className={`${th} text-right`}>증감액</th>
                        <th className={`${th} text-right`}>증감률</th>
                        <th className={`${th} text-right`}>건수</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[#E6E9ED]">
                    {rows.length === 0 && (
                        <tr><td className={`${td} text-center py-8 text-[#ADB5BD]`} colSpan={9}>데이터 없음</td></tr>
                    )}
                    {rows.map((r, i) => (
                        <tr key={`${r.townId ?? r.townName}-${i}`} className="hover:bg-[#F5F7FA]">
                            <td className={td}>{r.townName}</td>
                            <td className={td}>{r.category ?? '-'}</td>
                            <td className={`${td} text-right`}>{formatNumber(r.payAmount)}</td>
                            <td className={`${td} text-right`}>{formatNumber(r.pointAmount)}</td>
                            <td className={`${td} text-right font-semibold text-[#2A3F54]`}>{formatNumber(r.amount)}</td>
                            <td className={`${td} text-right`}>{formatNumber(r.prevAmount)}</td>
                            <td className={`${td} text-right ${r.diffAmount > 0 ? 'text-[#1ABB9C]' : r.diffAmount < 0 ? 'text-[#E74C3C]' : ''}`}>
                                {r.diffAmount > 0 ? '+' : ''}{formatNumber(r.diffAmount)}
                            </td>
                            <td className={`${td} text-right ${r.diffRate > 0 ? 'text-[#1ABB9C]' : r.diffRate < 0 ? 'text-[#E74C3C]' : ''}`}>
                                {r.prevAmount > 0 ? `${r.diffRate > 0 ? '+' : ''}${r.diffRate}%` : '-'}
                            </td>
                            <td className={`${td} text-right`}>{formatNumber(r.billCount)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
